<template>
  <div>
    <!-- Header Section -->
    <div class="row mb-4">
      <div class="col-md-4">
        <div class="d-flex align-items-center">
          <div class="bg-primary-light p-3 rounded-2 me-3">
            <vue-feather type="users" class="d-flex justify-content-center align-content-center" stroke="#652aa3" size="24"></vue-feather>
          </div>
          <h3 class="mb-0 fw-bold">Employee Management</h3>
        </div>
      </div>
      <div class="col-md-8">
        <div class="d-flex justify-content-end">
          <button
            v-if="profile.role !== 'user'"
            class="btn btn-outline-info shadow-sm"
            @click="openFileRequestModal"
          >
            <vue-feather type="file-text" size="18" class="me-1"></vue-feather>
            Request Files
          </button>
          <button
            v-if="profile.role !== 'user'"
            class="btn btn-primary shadow-sm ms-3"
            :class="subscription.employees <= employeeData.length ? 'disabled' : ''"
            @click="addEmployee"
          >
            <vue-feather type="user-plus" size="18" class="me-1"></vue-feather>
            Add Employee
          </button>
          <button
            v-if="profile.role !== 'user'"
            class="btn btn-outline-primary shadow-sm ms-3"
            @click="$refs.refInputAllowancesEl.click()"
          >
            <vue-feather type="upload" size="18" class="me-1"></vue-feather>
            Upload Allowances
            <i class="bi bi-info-circle fs-6 ms-1"
              data-bs-toggle="tooltip"
              data-bs-placement="bottom"
              data-bs-html="true"
              title="Upload an excel file with employee allowances data. Please contact admin for sample template">
            </i>
          </button>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <Card cardClasses="shadow-sm border-0 h-100 mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div class="font-size-125 color-gray-600">
              <vue-feather type="list" stroke="#652aa3" size="18" class="me-1"></vue-feather>
              Employees ({{filteredEmployees.length}})
            </div>
            <UInput v-model="dataTableSearch" size="md" placeholder="Search employee" class="mb-0 w-25 search-input"/>
          </div>

          <!-- Filters -->
          <div class="row g-3 mb-4">
            <div class="col-md-3">
              <label class="form-label small fw-semibold text-muted">Work Station</label>
              <select class="form-select form-select" v-model="filterWorkStation">
                <option value="">All Work Stations</option>
                <option v-for="station in workStations" :key="station.id" :value="station.id">
                  {{ station.name }}
                </option>
              </select>
            </div>
            <div class="col-md-3">
              <label class="form-label small fw-semibold text-muted">Department</label>
              <select class="form-select form-select" v-model="filterDepartment">
                <option value="">All Departments</option>
                <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                  {{ dept.name }}
                </option>
              </select>
            </div>
            <div class="col-md-3">
              <label class="form-label small fw-semibold text-muted">Designation</label>
              <select class="form-select form-select" v-model="filterDesignation">
                <option value="">All Designations</option>
                <option v-for="designation in designations" :key="designation.id" :value="designation.id">
                  {{ designation.name }}
                </option>
              </select>
            </div>
            <div class="col-md-3 d-flex align-items-end">
              <button class="btn btn-sm btn-outline-secondary w-100 py-2" @click="clearFilters">
                <vue-feather type="x" size="14" class="me-1"></vue-feather>
                Clear Filters
              </button>
            </div>
          </div>

          <!-- Hidden file inputs -->
          <input
            ref="refInputEl"
            type="file"
            name="logo"
            accept=".xlsx,.xls"
            hidden
            @input="uploadEmployeeFile"
          >
          <input
            ref="refInputAllowancesEl"
            type="file"
            name="logo"
            accept=".xlsx,.xls"
            hidden
            @input="uploadAllowancesFile"
          >

          <!-- Loading Spinner -->
          <div v-if="loading" class="text-center py-4">
            <Spinner spinner-class="mb-1" />
          </div>

          <!-- Employee Table -->
          <div v-if="!loading && paginatedEmployees.length > 0" class="table-responsive">
            <table class="table table-hover align-middle">
              <thead class="table-light">
                <tr>
                  <th scope="col" class="fw-semibold">#</th>
                  <th scope="col" class="fw-semibold">Employee No</th>
                  <th scope="col" class="fw-semibold">Name</th>
                  <th scope="col" class="fw-semibold">ID Number</th>
                  <th scope="col" class="fw-semibold">Phone</th>
                  <th scope="col" class="fw-semibold">Email</th>
                  <th scope="col" class="fw-semibold">Status</th>
                  <th scope="col" class="fw-semibold">Created</th>
                  <th scope="col" class="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in paginatedEmployees" :key="index" class="employee-row">
                  <td>{{ (dataTablePage - 1) * dataTablePerPage + index + 1 }}</td>
                  <td>{{ row[1] }}</td>
                  <td class="fw-medium">{{ row[2] }}</td>
                  <td>{{ row[3] }}</td>
                  <td>{{ row[4] }}</td>
                  <td>{{ row[5] }}</td>
                  <td>
                    <span class="badge" :class="'bg-' + getPillByStatus(row[6])">{{ row[6] }}</span>
                  </td>
                  <td>{{ row[7] }}</td>
                  <td class="text-end">
                    <button
                      class="btn btn-sm btn-outline-primary"
                      @click="employeeDetails(row[0])"
                    >
                      <vue-feather type="eye" size="16"></vue-feather>
                      Details
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Empty State -->
          <div v-if="!loading && paginatedEmployees.length === 0" class="text-center py-4">
            <div class="empty-state">
              <div class="bg-light p-4 rounded-circle mb-3 d-inline-block">
                <vue-feather type="users" size="32" stroke="#6c757d"></vue-feather>
              </div>
              <h5>No Employees Found</h5>
              <p class="text-muted">Start by adding your first employee</p>
              <button
                v-if="profile.role !== 'user'"
                class="btn btn-primary"
                :class="subscription.employees <= 0 ? 'disabled' : ''"
                @click="addEmployee"
              >
                <vue-feather type="user-plus" size="16" class="me-1"></vue-feather>
                Add Employee
              </button>
              <button
                v-if="profile.role === 'primary'"
                class="btn btn-outline-primary ms-3"
                @click="$refs.refInputEl.click()"
              >
                <vue-feather type="upload" size="16" class="me-1"></vue-feather>
                Upload Employees
              </button>
            </div>
          </div>

          <!-- Pagination -->
          <div v-if="!loading && paginatedEmployees.length > 0" class="pagination-container d-flex flex-wrap justify-content-around pt-3 pb-4">
            <div class="col-md-3">
              <button class="btn btn-outline-dark text-uppercase shadow-sm" @click="$router.go(-1)">
                <vue-feather type="arrow-left" size="16" class="me-1"></vue-feather>
                Back
              </button>
            </div>
            <div class="col-md-6 text-center">
              <p class="mb-2 small">Rows per page</p>
              <select class="form-select shadow-sm mx-auto rows-per-page" v-model="dataTablePerPage">
                <option v-for="item in [5, 10, 25, 50, 100]" :key="item" :value="item">{{item}}</option>
              </select>
            </div>
            <div class="col-md-3">
              <Pagination v-model="dataTablePage" :pages="Math.ceil(filteredEmployees.length / dataTablePerPage)">
                <template v-slot:next>
                  Next
                </template>
                <template v-slot:previous>
                  Prev
                </template>
              </Pagination>
            </div>
          </div>
        </Card>
      </div>
    </div>

    <!-- Request Files Modal -->
    <RequestFilesModal
      :open="fileRequestModal"
      :employees="activeEmployees"
      @closed="fileRequestModal = false"
      @success="handleFileRequestSuccess"
    />
  </div>
</template>

<script>
import employeeService from "@/api/employeeService";
import departmentService from "@/api/departmentService";
import designationService from "@/api/designationService";
import stationService from "@/api/stationService";
import formatDate from "@/utils/formatDate";
import router from "@/router";
import {decrypt} from "@/utils/crypto";
import allowanceService from "@/api/allowanceService";
import VueFeather from 'vue-feather';
import RequestFilesModal from '@/components/modals/RequestFilesModal.vue';

export default {
  name: "Employees",
  components: { VueFeather, RequestFilesModal },

  data(){
    return {
      profile: {},
      loading: true,
      dataTableHeaders: [
        '#',
        'Employee No',
        'Name',
        'ID Number',
        'Phone',
        'Email',
        'Status',
        'Created',
        null
      ],
      dataTable: [],
      dataTableSearch: '',
      dataTablePage: 1,
      dataTablePerPage: 10,
      subscription: {
        package: {}
      },
      departments: [],
      designations: [],
      workStations: [],
      filterDepartment: '',
      filterDesignation: '',
      filterWorkStation: '',
      fileRequestModal: false,
    }
  },

  methods:{
    getPillByStatus(cellData) {
      switch (cellData) {
        case 'inactive':
          return 'danger';
        case 'active':
          return 'success';
        case 'suspended':
          return 'warning';
        case 'terminated':
          return 'dark';
      }
    },
    async getEmployees() {
      this.loading = true
      try {
        this.dataTable = await employeeService.getEmployees();
      } catch (error) {
        console.error("Error fetching employees:", error)
      } finally {
        this.loading = false
      }
    },
    async getDepartments() {
      try {
        this.departments = await departmentService.getDepartments();
      } catch (error) {
        console.error("Error fetching departments:", error)
      }
    },
    async getDesignations() {
      try {
        this.designations = await designationService.getDesignations();
      } catch (error) {
        console.error("Error fetching designations:", error)
      }
    },
    async getWorkStations() {
      try {
        this.workStations = await stationService.getStations();
      } catch (error) {
        console.error("Error fetching work stations:", error)
      }
    },
    clearFilters() {
      this.filterDepartment = '';
      this.filterDesignation = '';
      this.filterWorkStation = '';
    },
    openFileRequestModal() {
      this.fileRequestModal = true;
    },
    handleFileRequestSuccess() {
      this.fileRequestModal = false;
    },
    employeeDetails(id) {
      router.push('/employee/' + id)
    },
    addEmployee(){
      router.push('/add-employee')
    },
    uploadEmployeeFile : async function(file) {
      const fileReader = new FileReader()
      const { files } = file.target
      let response = false
      if (files && files.length) {
        fileReader.readAsDataURL(files[0])
        fileReader.onload = async () => {
          if (typeof fileReader.result === 'string') {
            try {
              response = await employeeService.uploadEmployeeFile({ file: fileReader.result })
              if (response) {
                await this.getEmployees()
              }
            } catch (error) {
              console.error("Error uploading employee file:", error)
            }
          }
        }
      }
    },
    uploadAllowancesFile : async function(file) {
      const fileReader = new FileReader()
      const { files } = file.target
      if (files && files.length) {
        fileReader.readAsDataURL(files[0])
        fileReader.onload = async () => {
          if (typeof fileReader.result === 'string') {
            try {
              await allowanceService.uploadAllowancesFile({ file: fileReader.result })
            } catch (error) {
              console.error("Error uploading allowances file:", error)
            }
          }
        }
      }
    }
  },

  computed: {
    employeeData(){
      let data = [];
      for (let i = 0; i < this.dataTable.length; i++) {
        let fields = [
          this.dataTable[i].id,
          this.dataTable[i].employee_no,
          this.dataTable[i].first_name + ' ' + this.dataTable[i].last_name,
          this.dataTable[i].id_number,
          this.dataTable[i].phone,
          this.dataTable[i].email,
          this.dataTable[i].status,
          formatDate(this.dataTable[i].created_at)
        ];
        data.push(fields);
      }
      return data;
    },
    filteredEmployees() {
      let filtered = this.dataTable;

      // Filter by department
      if (this.filterDepartment) {
        filtered = filtered.filter(emp => emp.department_id === this.filterDepartment);
      }

      // Filter by designation
      if (this.filterDesignation) {
        filtered = filtered.filter(emp => emp.designation_id === this.filterDesignation);
      }

      // Filter by work station
      if (this.filterWorkStation) {
        filtered = filtered.filter(emp => emp.work_station_id === this.filterWorkStation);
      }

      // Convert to employeeData format
      let data = [];
      for (let i = 0; i < filtered.length; i++) {
        let fields = [
          filtered[i].id,
          filtered[i].employee_no,
          filtered[i].first_name + ' ' + filtered[i].last_name,
          filtered[i].id_number,
          filtered[i].phone,
          filtered[i].email,
          filtered[i].status,
          formatDate(filtered[i].created_at)
        ];
        data.push(fields);
      }

      // Apply search filter
      if (!this.dataTableSearch) {
        return data;
      }
      const term = this.dataTableSearch.toString().toLowerCase();
      return data.filter(row =>
        row.some(col => (col ? col.toString().toLowerCase().includes(term) : false))
      );
    },
    paginatedEmployees() {
      const start = (this.dataTablePage - 1) * this.dataTablePerPage;
      return this.filteredEmployees.slice(start, start + this.dataTablePerPage);
    },
    activeEmployees() {
      return this.dataTable.filter(emp => emp.status === 'active');
    }
  },

  created: async function () {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    await Promise.all([
      this.getDepartments(),
      this.getDesignations(),
      this.getWorkStations(),
      this.getEmployees()
    ]);
    this.subscription = JSON.parse(decrypt(sessionStorage.getItem('subscription')))
  },

  watch: {
    dataTableSearch() {
      this.dataTablePage = 1;
    },
    dataTablePerPage() {
      this.dataTablePage = 1;
    },
    filterDepartment() {
      this.dataTablePage = 1;
    },
    filterDesignation() {
      this.dataTablePage = 1;
    },
    filterWorkStation() {
      this.dataTablePage = 1;
    }
  },
}
</script>

<style scoped>
.bg-primary-light {
  background-color: rgba(101, 42, 163, 0.1);
}

.text-primary {
  color: #652aa3 !important;
}

.employee-row {
  transition: all 0.2s ease;
}

.employee-row:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.empty-state {
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.font-size-125 {
  font-size: 1.25rem;
}

.color-gray-600 {
  color: #6c757d;
}

.search-input {
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  border-radius: 0.25rem;
  transition: all 0.2s ease;
}

.search-input:focus-within {
  box-shadow: 0 2px 10px rgba(101, 42, 163, 0.15);
}

.btn {
  transition: all 0.2s ease;
}

.btn:hover:not(.disabled) {
  transform: translateY(-2px);
}

.badge {
  padding: 0.5em 0.75em;
}

.rows-per-page {
  width: 80px;
  margin: 0 auto;
}

.rows-per-page-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.form-select-sm {
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  border-radius: 0.25rem;
  transition: all 0.2s ease;
}

.form-select-sm:focus {
  box-shadow: 0 2px 10px rgba(101, 42, 163, 0.15);
  border-color: #652aa3;
}

.employee-list-container {
  background-color: #f8f9fa;
}

.employee-list-container::-webkit-scrollbar {
  width: 8px;
}

.employee-list-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.employee-list-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}

.employee-list-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
